import React, { useState, useEffect, useRef } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { toast } from 'react-toastify'
import extensionService from '../../../services/extensionService'
import { transformLoadForExtension } from './postTruckHelpers'

const POST_TRUCK_SETTINGS_KEY = 'postATruckSettings'

const getDefaultSettings = () => ({
	mileBuffer: 25,
	payoutOffset: -1,
	perMileOffset: -0.01,
	originRadius: 25,
	destinationRadius: 50,
	stemTime: '30',
	originDateStep: '-30',
	destinationDateStep: '30',
})

const loadSavedSettings = () => {
	try {
		const saved = localStorage.getItem(POST_TRUCK_SETTINGS_KEY)
		if (saved) {
			return { ...getDefaultSettings(), ...JSON.parse(saved) }
		}
	} catch (error) {
		console.error('Error loading post truck settings:', error)
	}
	return getDefaultSettings()
}

const calculateQuickPostData = (load, settings) => {
	const totalDistance = load.distance || 0
	const adjustedPrice = ((load.price || 0) + settings.payoutOffset).toFixed(2)
	const loadPricePerMile = (load.price || 0) / (totalDistance || 1)
	const adjustedPricePerMile = (
		loadPricePerMile + settings.perMileOffset
	).toFixed(2)
	const minMiles = Math.max(
		1,
		Math.round(totalDistance - settings.mileBuffer)
	).toString()
	const maxMiles = Math.round(totalDistance + settings.mileBuffer).toString()

	const now = new Date()
	const originalPickupTime = new Date(
		load.pickup?.date_local || new Date().toISOString()
	)
	const originalDeliveryTime = new Date(
		load.dropOff?.date_local || new Date().toISOString()
	)

	let updatedPickupTime, updatedDeliveryTime

	if (originalPickupTime.getTime() < now.getTime()) {
		updatedPickupTime = new Date(now.getTime() + 1.5 * 60 * 60 * 1000)
		const originalTransitTime =
			originalDeliveryTime.getTime() - originalPickupTime.getTime()
		const minTransitTime = 4 * 60 * 60 * 1000
		const transitTime = Math.max(originalTransitTime, minTransitTime)
		updatedDeliveryTime = new Date(
			updatedPickupTime.getTime() + transitTime + 3 * 60 * 60 * 1000
		)
	} else {
		updatedPickupTime = new Date(originalPickupTime.getTime() - 15 * 60 * 1000)
		updatedDeliveryTime = new Date(
			originalDeliveryTime.getTime() + 30 * 60 * 1000
		)
	}

	return {
		adjustedPrice,
		adjustedPricePerMile,
		minMiles,
		maxMiles,
		stemTime: settings.stemTime,
		originRadius: settings.originRadius,
		destinationRadius: settings.destinationRadius,
		updatedPickupTime,
		updatedDeliveryTime,
	}
}

function QuickPostButton({
	load,
	isDarkMode,
	isActive,
	onActivate,
	onDeactivate,
}) {
	const [status, setStatus] = useState('idle')
	const [message, setMessage] = useState('')
	const buttonRef = useRef(null)
	const timeoutRef = useRef(null)

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	useEffect(() => {
		if (!isActive && status === 'confirm') {
			setStatus('idle')
		}
	}, [isActive, status])

	const handleQuickPost = async e => {
		e.stopPropagation()

		if (status === 'idle') {
			setStatus('confirm')
			onActivate?.()

			timeoutRef.current = setTimeout(() => {
				setStatus('idle')
				onDeactivate?.()
			}, 7000)
			return
		}

		if (status === 'confirm') {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			setStatus('posting')
			setMessage('')

			try {
				const settings = loadSavedSettings()
				const quickPostData = calculateQuickPostData(load, settings)
				const transformedLoadData = transformLoadForExtension(
					load,
					quickPostData
				)

				const result = await extensionService.postTruck(transformedLoadData)

				if (result.success) {
					setStatus('success')
					setMessage('Posted!')

					timeoutRef.current = setTimeout(() => {
						setStatus('idle')
						setMessage('')
						onDeactivate?.()
					}, 2000)
				} else {
					setStatus('error')
					toast.error(result.error || 'Failed to post truck', { position: 'top-center' })

					timeoutRef.current = setTimeout(() => {
						setStatus('idle')
						onDeactivate?.()
					}, 3000)
				}
			} catch (error) {
				console.error('Quick post error:', error)
				setStatus('error')
				toast.error(error.message || 'An error occurred', { position: 'top-center' })

				timeoutRef.current = setTimeout(() => {
					setStatus('idle')
					onDeactivate?.()
				}, 3000)
			}
		}
	}

	const handleCancel = e => {
		e.stopPropagation()
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		setStatus('idle')
		setMessage('')
		onDeactivate?.()
	}

	const getButtonContent = () => {
		switch (status) {
			case 'confirm':
				return (
					<>
						<CheckIcon className="h-3 w-3" />
						<span>Confirm</span>
					</>
				)
			case 'posting':
				return (
					<>
						<div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
						<span>Posting...</span>
					</>
				)
			case 'success':
				return (
					<>
						<CheckIcon className="h-3 w-3" />
						<span>{message}</span>
					</>
				)
			case 'error':
				return (
					<>
						<XMarkIcon className="h-3 w-3" />
						<span>Failed</span>
					</>
				)
			default:
				return <span>QuickPost</span>
		}
	}

	const getButtonStyles = () => {
		const baseStyles =
			'flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-all duration-200'

		switch (status) {
			case 'confirm':
				return `${baseStyles} bg-green-600 hover:bg-green-700 text-white`
			case 'posting':
				return `${baseStyles} bg-blue-600 text-white cursor-wait`
			case 'success':
				return `${baseStyles} bg-green-600 text-white`
			case 'error':
				return `${baseStyles} bg-red-600 text-white`
			default:
				return `${baseStyles} bg-[#1e6feb] hover:bg-[#1a5fd4] text-white`
		}
	}

	return (
		<div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
			{status === 'confirm' && (
				<button
					onClick={handleCancel}
					className={`flex items-center px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${
						isDarkMode
							? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
							: 'bg-gray-200 hover:bg-gray-300 text-gray-600'
					}`}
				>
					<XMarkIcon className="h-3 w-3" />
				</button>
			)}

			<button
				ref={buttonRef}
				onClick={handleQuickPost}
				disabled={status === 'posting' || status === 'success'}
				className={getButtonStyles()}
			>
				{getButtonContent()}
			</button>
		</div>
	)
}

export default QuickPostButton
